import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { DateMaker } from '../../../../lib/helpers';
import Text from '../../../../shared/Text/Text';
import { pluralize } from '../../../../shared/ProgressBarComponent/ProgressBarHelpers';

const PrizeBox = styled.div`
  font-family: 'Montserrat', sans-serif;
  background: ${themeGet('colors.white')};
  margin: auto;
  width: 360px;
  max-width: 100%;
  border: 1px solid ${themeGet('colors.black.20')};
  border-radius: 8px;
  text-align: left;
`;

const PrizeHeader = styled.div`
  position: relative;
  padding: 11px 16px;
`;

const StatusRow = styled.div`
  height: 70px;
  color: ${themeGet('colors.white')};
  text-align: center;
  border-bottom: 1px solid ${themeGet('colors.white')};
`;

const TicketsRow = styled(StatusRow)`
  background: ${themeGet('colors.primary.100')};
`;

const TimeRow = styled(StatusRow)`
  background: ${themeGet('colors.black.80')};
`;

const IconContainer = styled.div`
  float: left;
  width: 70px;
  height: 100%;
  text-align: center;
  color: $white;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InfoContainer = styled.div`
  border-left: 1px solid ${themeGet('colors.white')};
  float: none;
  overflow: hidden;
  width: auto;
  padding-bottom: 13px;
`;

const TimeContainer = styled(InfoContainer)`
  display: flex;
  justify-content: space-around;
`;

const Count = styled.h1`
  color: ${themeGet('colors.white')};
  font-size: 26px;
  font-weight: 500;
  line-height: 18px;
  margin: 16px 0 8px 0;
`;

const TimeCell = styled.div`
  width: 50%;
  padding-bottom: 13px;
  float: left;
  text-transform: capitalize;
`;

const ItemRow = styled.div`
  border-bottom: 1px solid ${themeGet('colors.black.20')};
  height: 70px;
  text-align: center;
`;

const LastRow = styled(ItemRow)`
  border-bottom: none;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  height: 70px;
  padding: 8px 16px;
  word-break: break-word;
  border-left: 1px solid ${themeGet('colors.black.20')};
`;

const Prizes = ({ prizes }) => {
  const { t } = useTranslation('users');

  return prizes.map((prize, key) => (
    <ItemRow key={key}>
      <IconContainer>
        <i className="fa-light fa-gift fa-2x" alt="gift"></i>
      </IconContainer>
      <DescriptionContainer>
        <Text variant="small1" color="black.80" as="div">
          {prize || t('prizesModal.prizePlaceholder')}
        </Text>
      </DescriptionContainer>
    </ItemRow>
  ));
};

const PrizePreview = ({ prize, ticketsCount }) => {
  const { t } = useTranslation('users');
  const { maxDate, prizeDescriptions, message } = prize;
  const timeLeft = prize.remainingInfo;

  const parseDate = date => {
    const formattedDate = new Date(date);
    return `Ends ${DateMaker.spelledOutDays[formattedDate.getDay()]}, ${
      DateMaker.spelledOutMonths[formattedDate.getMonth()]
    } ${formattedDate.getDate()}`;
  };

  return (
    <div>
      <Text variant="small1" color="black.80" as="div">
        {t('prizesModal.prizePreview')}
      </Text>
      <PrizeBox className="prize_box">
        <PrizeHeader>
          <Text
            variant="h6"
            color="black.100"
            as="div"
            className="js-prize-header"
          >
            {t('prizesModal.prizeStatus')}
          </Text>
          <Text
            variant="subtitle2"
            color="black.80"
            as="div"
            className="end_date"
          >
            {maxDate ? parseDate(maxDate) : t('prizesModal.endsAtPlaceholder')}
          </Text>
        </PrizeHeader>
        <TicketsRow className="tickets_row">
          <IconContainer>
            <i className="fa-light fa-trophy-star fa-2x" alt="trophy"></i>
          </IconContainer>
          <InfoContainer>
            <Count>{ticketsCount}</Count>
            <Text variant="subtitle2" color="white" as="div">
              Entries
            </Text>
          </InfoContainer>
        </TicketsRow>
        <TimeRow>
          <IconContainer>
            <i className="fa-light fa-clock fa-2x" alt="trophy"></i>
          </IconContainer>
          <TimeContainer>
            {timeLeft.weeks > 0 && (
              <TimeCell>
                <Count id="first_date_num">{timeLeft.weeks}</Count>
                <Text
                  variant="subtitle2"
                  color="white"
                  as="div"
                  id="first_date_text"
                >
                  {pluralize(t('prizesModal.week'), timeLeft.weeks)}
                </Text>
              </TimeCell>
            )}
            <TimeCell>
              <Count id="second_date_num">{timeLeft.days || 0}</Count>
              <Text
                variant="subtitle2"
                color="white"
                as="div"
                id="second_date_text"
              >
                {pluralize(t('prizesModal.day'), timeLeft.days || 0)}
              </Text>
            </TimeCell>
            {timeLeft.weeks === 0 && (
              <TimeCell>
                <Count id="second_date_num">{timeLeft.hours || 0}</Count>
                <Text
                  variant="subtitle2"
                  color="white"
                  as="div"
                  id="second_date_text"
                >
                  {pluralize(t('prizesModal.hour'), timeLeft.hours || 0)}
                </Text>
              </TimeCell>
            )}
          </TimeContainer>
        </TimeRow>
        <div>
          <Prizes prizes={prizeDescriptions} />
        </div>
        <ItemRow>
          <IconContainer>
            <i className="fa-light fa-ticket fa-2x" alt="trophy"></i>
          </IconContainer>
          <DescriptionContainer>
            <Text variant="small1" color="black.80" as="div">
              {t('prizesModal.pointsDescription')}
            </Text>
          </DescriptionContainer>
        </ItemRow>
        <LastRow>
          <IconContainer>
            <i className="fa-light fa-party-horn fa-2x" alt="trophy"></i>
          </IconContainer>
          <DescriptionContainer>
            <Text variant="small1" color="black.80" as="div">
              {message}
            </Text>
          </DescriptionContainer>
        </LastRow>
      </PrizeBox>
    </div>
  );
};

export default PrizePreview;
